// src/store/catalogSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  setDoc, // ARQ FIX: Importamos setDoc para poder definir IDs manuales
} from "firebase/firestore";
// ARQ Validada: Ruta al archivo de config de Firebase
import { db } from "../api/firebase";

// --- Thunks Asíncronos ---

export const fetchCatalogSection = createAsyncThunk(
  "catalog/fetchSection",
  async ({ tenantId, sectionKey }, { rejectWithValue }) => {
    try {
      if (!tenantId || !sectionKey) {
        throw new Error("Tenant ID y Section Key son requeridos.");
      }
      const itemsCollection = collection(db, "tenants", tenantId, "catalog");
      const q = query(itemsCollection, where("sectionKey", "==", sectionKey));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { sectionKey, items };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCatalogItem = createAsyncThunk(
  "catalog/createItem",
  async ({ tenantId, itemData }, { rejectWithValue }) => {
    try {
      if (!tenantId || !itemData || !itemData.sectionKey) {
        throw new Error(
          "Tenant ID y itemData (con sectionKey) son requeridos."
        );
      }

      // ARQ FIX: Lógica condicional para usar ID Semántico vs ID Automático

      // 1. Si el itemData ya trae un ID (generado por contentIdGenerator), usamos ese.
      if (itemData.id) {
        // Referencia exacta al documento: tenants/{id}/catalog/{ID_SEMANTICO}
        const itemDocRef = doc(db, "tenants", tenantId, "catalog", itemData.id);

        // setDoc fuerza la creación con ese ID específico
        await setDoc(itemDocRef, itemData);

        // Retornamos el item tal cual (ya tiene su ID)
        return { item: itemData };
      }
      // 2. Fallback: Si no trae ID, dejamos que Firestore genere uno (lógica anterior)
      else {
        const itemsCollection = collection(db, "tenants", tenantId, "catalog");
        const docRef = await addDoc(itemsCollection, itemData);
        return { item: { id: docRef.id, ...itemData } };
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCatalogItem = createAsyncThunk(
  "catalog/updateItem",
  async ({ tenantId, docId, itemData }, { rejectWithValue }) => {
    try {
      if (!tenantId || !docId || !itemData) {
        throw new Error("Tenant ID, Doc ID y itemData son requeridos.");
      }
      const itemDocRef = doc(db, "tenants", tenantId, "catalog", docId);
      await updateDoc(itemDocRef, itemData);
      return { docId, itemData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// --- ARQ: Estado Inicial y Slice (Sin cambios requeridos aquí) ---

const initialState = {};

const catalogSlice = createSlice({
  name: "catalog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- Fetch Section ---
      .addCase(fetchCatalogSection.pending, (state, action) => {
        const { sectionKey } = action.meta.arg;
        state[sectionKey] = {
          items: state[sectionKey]?.items || [],
          status: "loading",
        };
      })
      .addCase(fetchCatalogSection.fulfilled, (state, action) => {
        const { sectionKey, items } = action.payload;
        state[sectionKey] = { items, status: "succeeded" };
      })
      .addCase(fetchCatalogSection.rejected, (state, action) => {
        const { sectionKey } = action.meta.arg;
        state[sectionKey] = {
          items: [],
          status: "failed",
          error: action.payload,
        };
      })

      // --- Create Item ---
      .addCase(createCatalogItem.pending, (state, action) => {
        const { sectionKey } = action.meta.arg.itemData;
        if (state[sectionKey]) {
          state[sectionKey].status = "saving";
        }
      })
      .addCase(createCatalogItem.fulfilled, (state, action) => {
        const { item } = action.payload;
        const { sectionKey } = item;

        if (state[sectionKey]) {
          state[sectionKey].items.push(item);
          state[sectionKey].status = "succeeded";
        } else {
          state[sectionKey] = { items: [item], status: "succeeded" };
        }
      })
      .addCase(createCatalogItem.rejected, (state, action) => {
        const { sectionKey } = action.meta.arg.itemData;
        if (state[sectionKey]) {
          state[sectionKey].status = "failed";
          state[sectionKey].error = action.payload;
        }
      })

      // --- Update Item ---
      .addCase(updateCatalogItem.pending, (state, action) => {
        const { sectionKey } = action.meta.arg.itemData;
        if (state[sectionKey]) {
          state[sectionKey].status = "saving";
        }
      })
      .addCase(updateCatalogItem.fulfilled, (state, action) => {
        const { docId, itemData } = action.payload;
        const { sectionKey } = itemData;

        if (state[sectionKey]) {
          const index = state[sectionKey].items.findIndex(
            (item) => item.id === docId
          );
          if (index !== -1) {
            state[sectionKey].items[index] = {
              ...state[sectionKey].items[index],
              ...itemData,
            };
          }
          state[sectionKey].status = "succeeded";
        }
      })
      .addCase(updateCatalogItem.rejected, (state, action) => {
        const { sectionKey } = action.meta.arg.itemData;
        if (state[sectionKey]) {
          state[sectionKey].status = "failed";
          state[sectionKey].error = action.payload;
        }
      });
  },
});

export default catalogSlice.reducer;
